import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import ItemCreation from './components/ItemCreation';
import ItemsList from './components/ItemsList';
import ItemDetail from './components/ItemDetail';
import BrandManagement from './components/BrandManagement';
import CategoryManagement from './components/CategoryManagement';
import Checkout from './components/Checkout';
import TransactionHistory from './components/TransactionHistory';
import { squareAPI } from './services/squareAPI';
import { loadSampleData, clearAllData } from './utils/sampleData';

function Navigation({ items, onClearAllData }) {
  const location = useLocation();

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex space-x-8">
            <Link
              to="/"
              className={`inline-flex items-center px-1 pt-1 text-sm font-medium border-b-2 transition-colors ${
                isActive('/') && !location.pathname.includes('/item/')
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-900 border-transparent hover:border-gray-300'
              }`}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Inventory
            </Link>
            <Link
              to="/create"
              className={`inline-flex items-center px-1 pt-1 text-sm font-medium border-b-2 transition-colors ${
                isActive('/create')
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-900 border-transparent hover:border-gray-300'
              }`}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Item
            </Link>
            <Link
              to="/brands"
              className={`inline-flex items-center px-1 pt-1 text-sm font-medium border-b-2 transition-colors ${
                isActive('/brands')
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-900 border-transparent hover:border-gray-300'
              }`}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
              </svg>
              Brands
            </Link>
            <Link
              to="/categories"
              className={`inline-flex items-center px-1 pt-1 text-sm font-medium border-b-2 transition-colors ${
                isActive('/categories')
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-900 border-transparent hover:border-gray-300'
              }`}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              Categories
            </Link>
            <Link
              to="/checkout"
              className={`inline-flex items-center px-1 pt-1 text-sm font-medium border-b-2 transition-colors ${
                isActive('/checkout')
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-900 border-transparent hover:border-gray-300'
              }`}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Checkout
            </Link>
            <Link
              to="/transactions"
              className={`inline-flex items-center px-1 pt-1 text-sm font-medium border-b-2 transition-colors ${
                isActive('/transactions')
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-900 border-transparent hover:border-gray-300'
              }`}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Transactions
            </Link>
          </div>
          <div className="flex items-center">
            <span className="text-sm text-gray-500">
              {items.length} items in inventory
            </span>
            {items.length > 0 && (
              <button
                onClick={onClearAllData}
                className="ml-4 text-xs text-red-600 hover:text-red-700"
                title="Clear all data"
              >
                Clear All
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

function App() {
  const [items, setItems] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    loadItems();
    loadBrands();
    loadCategories();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      const fetchedItems = await squareAPI.getItems();
      setItems(fetchedItems);
      setShowWelcome(fetchedItems.length === 0 && squareAPI.isMock());
    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBrands = async () => {
    try {
      const fetchedBrands = await squareAPI.getBrands();
      setBrands(fetchedBrands);
    } catch (error) {
      console.error('Error loading brands:', error);
    }
  };

  const addBrand = async (name) => {
    try {
      const updated = await squareAPI.addBrand(name);
      setBrands(updated);
    } catch (error) {
      console.error('Error adding brand:', error);
    }
  };

  const deleteBrand = async (name) => {
    try {
      const updated = await squareAPI.deleteBrand(name);
      setBrands(updated);
    } catch (error) {
      console.error('Error deleting brand:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const fetchedCategories = await squareAPI.getCategories();
      setCategories(fetchedCategories);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const addCategory = async (name) => {
    try {
      const updated = await squareAPI.addCategory(name);
      setCategories(updated);
    } catch (error) {
      console.error('Error adding category:', error);
    }
  };

  const deleteCategory = async (name) => {
    try {
      const updated = await squareAPI.deleteCategory(name);
      setCategories(updated);
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const handleLoadSampleData = () => {
    const sampleItems = loadSampleData();
    setItems(sampleItems);
    loadBrands();
    loadCategories();
    setShowWelcome(false);
    alert('Sample data loaded successfully! You can now explore the features.');
  };

  const handleClearAllData = () => {
    if (window.confirm('This will delete all items, brands, and categories. Are you sure?')) {
      clearAllData();
      setItems([]);
      setBrands([]);
      setCategories([]);
      setShowWelcome(true);
    }
  };

  const addItem = async (item) => {
    try {
      const newItem = await squareAPI.createItem(item);
      setItems([...items, newItem]);
      return newItem;
    } catch (error) {
      console.error('Error adding item:', error);
      throw error;
    }
  };

  const updateItem = async (id, updates) => {
    try {
      const updatedItem = await squareAPI.updateItem(id, updates);
      setItems(items.map(item => item.id === id ? updatedItem : item));
      return updatedItem;
    } catch (error) {
      console.error('Error updating item:', error);
      throw error;
    }
  };

  const deleteItem = async (id) => {
    try {
      await squareAPI.deleteItem(id);
      setItems(items.filter(item => item.id !== id));
    } catch (error) {
      console.error('Error deleting item:', error);
      throw error;
    }
  };

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        {/* Navigation */}
        <Navigation items={items} onClearAllData={handleClearAllData} />

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {showWelcome && !loading ? (
            <div className="max-w-2xl mx-auto text-center">
              <div className="bg-white rounded-lg shadow-lg p-12">
                <svg className="mx-auto h-16 w-16 text-blue-600 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Welcome to Your Inventory System! 👋
                </h2>
                
                <p className="text-lg text-gray-600 mb-8">
                  Get started by loading sample data to explore features, or create your first item.
                </p>

                <div className="space-y-4">
                  <button
                    onClick={handleLoadSampleData}
                    className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Load Sample Data (5 Items)
                  </button>
                  
                  <Link
                    to="/create"
                    className="w-full px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium flex items-center justify-center"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create Your First Item
                  </Link>
                </div>

                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Key Features:</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm text-gray-600">
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Multi-step item creation
                    </div>
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Automatic swing tags
                    </div>
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Color & size variations
                    </div>
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Square API integration
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <Routes>
              <Route
                path="/"
                element={
                  <ItemsList
                    items={items}
                    loading={loading}
                    onRefresh={loadItems}
                    onDelete={deleteItem}
                  />
                }
              />
              <Route
                path="/create"
                element={
                  <ItemCreation
                    onItemCreated={addItem}
                    brands={brands}
                    onCreateBrand={addBrand}
                    categories={categories}
                    onCreateCategory={addCategory}
                  />
                }
              />
              <Route
                path="/item/:id"
                element={
                  <ItemDetail
                    items={items}
                    onUpdate={updateItem}
                    onDelete={deleteItem}
                    brands={brands}
                    onCreateBrand={addBrand}
                    categories={categories}
                    onCreateCategory={addCategory}
                  />
                }
              />
              <Route
                path="/brands"
                element={
                  <BrandManagement
                    brands={brands}
                    items={items}
                    onAddBrand={addBrand}
                    onDeleteBrand={deleteBrand}
                  />
                }
              />
              <Route
                path="/categories"
                element={
                  <CategoryManagement
                    categories={categories}
                    items={items}
                    onAddCategory={addCategory}
                    onDeleteCategory={deleteCategory}
                  />
                }
              />
              <Route
                path="/checkout"
                element={<Checkout />}
              />
              <Route
                path="/transactions"
                element={<TransactionHistory />}
              />
            </Routes>
          )}
        </main>
      </div>
    </Router>
  );
}

export default App;
